import { DATA_ROOM_INVITE_TTL_MS } from 'shared';

import { localStorageUtil } from '../../utils/local-storage.util';

/**
 * An invitation the person has started accepting but has not finished, because accepting needs an
 * account and getting one takes them out of the app: sign up, confirm the address, come back. A
 * query parameter does not survive that detour, which is why the stakeholder invitation carries
 * its own store too.
 */
export interface DataRoomInviteStoreValue {
  /** When the link was followed, so an abandoned one cannot ambush a later sign-in. */
  createdAt: number;
  /** Set by the accept, so the room can be opened once the space list has loaded. */
  dataRoomId?: string;
  email: string;
  isAccepted: boolean;
  token: string;
}

export const dataRoomInviteStore = localStorageUtil<DataRoomInviteStoreValue>('dataRoomInvite');

/**
 * The stored invitation, or nothing — and a stale one is cleared on the way out.
 *
 * Somebody who followed a link and never finished signing up would otherwise be redirected into a
 * Data Room on every later sign-in, under any account, with a mismatch error to show for it. The
 * entry cannot outlive the invitation it stands for, so it expires on the invitation's own clock
 * rather than a second number of its own.
 */
export const readPendingDataRoomInvite = (
  now: number = Date.now(),
): DataRoomInviteStoreValue | null => {
  const invite = dataRoomInviteStore.get();

  if (!invite?.token) return null;

  if (!invite.createdAt || now - invite.createdAt >= DATA_ROOM_INVITE_TTL_MS) {
    dataRoomInviteStore.delete();

    return null;
  }

  return invite;
};
